// ── Lightweight "You won a real card!" toast (display-only). ──
// Triggered by the multiplayer `reward` ServerMessage (see @mn/shared protocol + the
// rewards-ingame.md "live moment"). This is purely cosmetic: the card NFT is airdropped
// on-chain by apps/rewards regardless. Renders a top-center banner that auto-dismisses.

import Phaser from '../lib/phaser.js';
import { KENNEY_FUTURE_NARROW_FONT_NAME } from '../assets/font-keys.js';

const VISIBLE_MS = 5200;
const RARITY_COLOR: Record<string, number> = {
  epic: 0xb16bff,
  rare: 0x3ad0ff,
  uncommon: 0x7af0c0,
  common: 0xd0d0d0,
};

/**
 * Show a celebratory reward banner on the given scene. Safe to call repeatedly; each call
 * stacks a fresh banner that fades itself out. No game state is touched.
 */
export function showRewardToast(scene: Phaser.Scene, cardName: string, rarity: string): void {
  const rarityKey = (rarity || 'common').toLowerCase();
  const accent = RARITY_COLOR[rarityKey] ?? RARITY_COLOR.common;
  const width = 560;
  const height = 96;
  const x = scene.scale.width / 2;
  const y = 70;

  const bg = scene.add.rectangle(0, 0, width, height, 0x0b1020, 0.92);
  bg.setStrokeStyle(3, accent, 1);

  const headline = scene.add
    .text(0, -22, '⭐ You won a real card!', {
      fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME,
      color: '#ffffff',
      fontSize: '26px',
    })
    .setOrigin(0.5);

  const detail = scene.add
    .text(0, 18, `${cardName} · ${rarityKey.toUpperCase()}`, {
      fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME,
      color: Phaser.Display.Color.IntegerToColor(accent).rgba,
      fontSize: '20px',
    })
    .setOrigin(0.5);

  const container = scene.add
    .container(x, y, [bg, headline, detail])
    .setScrollFactor(0)
    .setDepth(2000)
    .setAlpha(0);

  scene.tweens.add({ targets: container, alpha: 1, y: y + 10, duration: 300, ease: 'Quad.easeOut' });
  scene.tweens.add({
    targets: container,
    alpha: 0,
    delay: VISIBLE_MS,
    duration: 400,
    onComplete: () => container.destroy(),
  });
}
